import { useState, useEffect } from 'react'
import api from '../api'
import { Badge } from './types'
import type { LMNResult as LMNResultType, ICD10Result, PlanResult } from './types'
import WhatsNext from './WhatsNext'

interface LMNResultProps {
  result: LMNResultType
  editedLetter: string
  setEditedLetter: (val: string) => void
  deniedProduct: string
  carrier: string
  denialReason: string
  selectedIcd10: ICD10Result[]
  selectedPlan: PlanResult | null
  whatsNextCollapsed: boolean
  setWhatsNextCollapsed: (val: boolean) => void
  onOpenAppeals: () => void
  onGenerateP2P: () => void
  p2pLoading: boolean
  onSaveAsTemplate: () => void
  saveTemplateSuccess: boolean
  demoMode?: boolean
}

export default function LMNResult({
  result,
  editedLetter,
  setEditedLetter,
  deniedProduct,
  carrier,
  denialReason,
  selectedIcd10,
  selectedPlan,
  whatsNextCollapsed,
  setWhatsNextCollapsed,
  onOpenAppeals,
  onGenerateP2P,
  p2pLoading,
  onSaveAsTemplate,
  saveTemplateSuccess,
  demoMode = false,
}: LMNResultProps) {
  const [activeTab, setActiveTab] = useState<'letter' | 'citations' | 'checklist'>('letter')
  const [copied, setCopied] = useState(false)
  const [gradeResult, setGradeResult] = useState<{ grade: string; score: number; tips: string[] } | null>(null)
  const [tipsOpen, setTipsOpen] = useState(false)

  useEffect(() => {
    if (demoMode || !editedLetter) return
    api.post('/api/lmn/grade', {
      letter_text: editedLetter,
      letter_sections: result.letter_sections || {},
      carrier,
      denied_product: deniedProduct,
      denial_reason: denialReason,
      icd10_codes: selectedIcd10.map(c => c.code),
    })
      .then(res => setGradeResult(res.data))
      .catch(() => { /* non-fatal — grader is best-effort */ })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = () => {
    navigator.clipboard.writeText(editedLetter)
    setCopied(true); setTimeout(() => setCopied(false), 2000)
  }

  const handleDownload = async (type: 'txt' | 'docx' | 'pdf') => {
    if (type === 'txt') {
      const blob = new Blob([editedLetter], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url
      a.download = `LMN_${deniedProduct.replace(/\s+/g,'_')}_${carrier.replace(/\s+/g,'_')}.txt`
      a.click(); URL.revokeObjectURL(url)
      return
    }

    try {
      const res = await api.post(`/api/export/${type}`, {
        letter_text: editedLetter,
        drug_name: deniedProduct,
        carrier: carrier,
        diagnosis_codes: selectedIcd10.map(s => s.code),
        citations: result?.citations || [],
      }, { responseType: 'blob' })

      const mimeType = type === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf'
      const blob = new Blob([res.data], { type: mimeType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url
      const ext = type === 'docx' ? 'docx' : 'pdf'
      a.download = `LMN_${deniedProduct.replace(/\s+/g,'_')}_${carrier.replace(/\s+/g,'_')}.${ext}`
      a.click(); URL.revokeObjectURL(url)
    } catch (e) {
      /* export failed — silently ignore */
    }
  }

  return (
    <div className="space-y-4">
      {/* Status bar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between shadow-sm flex-wrap gap-3">
        <div className="flex items-center gap-2.5 flex-wrap">
          <Badge color="bg-emerald-50 text-emerald-600 border border-emerald-200">&#10003; Generated</Badge>
          <Badge color={result.generation_mode === 'live_ai' ? 'bg-violet-50 text-violet-600 border border-violet-200' : 'bg-slate-50 text-slate-500 border border-slate-200'}>
            {result.generation_mode === 'live_ai' ? 'AI-Powered' : 'Template'}
          </Badge>
          {result.citations.length > 0 && (
            <Badge color="bg-amber-50 text-amber-600 border border-amber-200">{result.citations.length} citations</Badge>
          )}
          <span className="text-xs text-slate-400 font-medium">{result.denial_rebuttal_strategy}</span>
          {gradeResult && (() => {
            const g = gradeResult.grade
            const color = g === 'A' ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
              : g === 'B' ? 'bg-teal-50 text-teal-600 border-teal-200'
              : g === 'C' ? 'bg-amber-50 text-amber-600 border-amber-200'
              : g === 'D' ? 'bg-orange-50 text-orange-600 border-orange-200'
              : 'bg-red-50 text-red-600 border-red-200'
            return (
              <button
                onClick={() => setTipsOpen(o => !o)}
                className={`text-xs px-2.5 py-1 rounded-lg border font-bold ${color} hover:opacity-80 transition-opacity`}
                title="Letter quality score — click for improvement tips"
              >
                {g} · {gradeResult.score}/100
              </button>
            )
          })()}
        </div>
        <div className="flex gap-2 flex-wrap">
          {demoMode ? (
            <span className="text-xs px-4 py-2 rounded-xl bg-slate-100 text-slate-400 font-semibold border border-slate-200 cursor-not-allowed">
              Sign up to copy, download & edit letters
            </span>
          ) : (<>
          <button onClick={handleCopy}
            className="text-xs px-4 py-2 rounded-xl bg-white hover:bg-teal-50 text-teal-600 font-semibold transition-all border border-slate-200 hover:border-teal-300">
            {copied ? '\u2713 Copied!' : 'Copy'}
          </button>
          <button onClick={() => handleDownload('docx')}
            className="text-xs px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-all shadow-sm flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6zm7 1.5L18.5 9H13V3.5zM8 13h8v1.5H8V13zm0 3h8v1.5H8V16zm0-6h3v1.5H8V10z"/></svg>
            Word Doc
          </button>
          <button onClick={() => handleDownload('pdf')}
            className="text-xs px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold transition-all shadow-sm flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6zm7 1.5L18.5 9H13V3.5zM8.5 11h2c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5H9.5V16H8.5v-5zm1 2.25h.75c.41 0 .75-.34.75-.75s-.34-.75-.75-.75H9.5v1.5zm4-2.25h1.5c1.1 0 2 .9 2 2v1c0 1.1-.9 2-2 2h-1.5v-5zm1 4.25h.5c.55 0 1-.45 1-1v-1c0-.55-.45-1-1-1h-.5v3z"/></svg>
            PDF
          </button>
          <button onClick={() => handleDownload('txt')}
            className="text-xs px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold transition-all">
            .txt
          </button>
          </>)}
          <button onClick={onGenerateP2P}
            disabled={p2pLoading}
            className="text-xs px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-all shadow-sm flex items-center gap-1.5 disabled:opacity-50">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
            </svg>
            {p2pLoading ? 'Generating...' : 'P2P Prep Sheet'}
          </button>
          <button
            onClick={onSaveAsTemplate}
            className="text-xs px-4 py-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-600 font-semibold transition-all border border-amber-200 hover:border-amber-300 flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Save as Template
          </button>
        </div>
        {saveTemplateSuccess && (
          <div className="mt-2 flex items-center gap-2 text-xs text-emerald-600 font-medium">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Template saved! You can reuse it from the Saved Templates dropdown.
          </div>
        )}
      </div>

      {gradeResult && tipsOpen && gradeResult.tips.length > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-xs font-bold text-violet-700 uppercase tracking-wide">
              {gradeResult.tips.length === 1 ? '1 way' : `${gradeResult.tips.length} ways`} to strengthen this letter
            </p>
            <button onClick={() => setTipsOpen(false)} className="text-violet-400 hover:text-violet-600 text-xs">✕</button>
          </div>
          <ul className="space-y-1.5">
            {gradeResult.tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-violet-800">
                <span className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center font-bold text-[10px]">{i + 1}</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {selectedPlan?.unverified && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-start gap-2.5">
          <span className="text-amber-500 text-lg leading-none">&#9888;</span>
          <div>
            <p className="text-sm font-semibold text-amber-700">Unverified Plan. Review Carefully.</p>
            <p className="text-xs text-amber-600 mt-1">
              This letter was generated using an unverified plan ("{selectedPlan.plan_name}").
              Formulary data may be incomplete or inaccurate. Please verify drug coverage details
              against the actual plan formulary before submitting this letter.
            </p>
          </div>
        </div>
      )}

      {result.confidence_note && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-sm text-amber-700">
          {result.confidence_note}
        </div>
      )}

      {result.diagnosis_descriptions.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-xs text-slate-600 space-y-1.5 shadow-sm">
          <span className="font-bold text-slate-500 text-[10px] uppercase tracking-wider">Diagnosis Codes</span>
          {result.diagnosis_descriptions.map((d, i) => (
            <div key={i} className="ml-1 flex items-start gap-2">
              <span className="text-teal-500 mt-0.5 font-bold">&bull;</span>
              <span>{d}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm dark:bg-slate-800 dark:border-slate-700 overflow-hidden">
        <div className="flex border-b border-slate-100 dark:border-slate-700">
          {(['letter', 'citations', 'checklist'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-6 py-3.5 text-sm font-semibold transition-all relative
                ${activeTab === tab ? 'text-teal-600' : 'text-slate-400 hover:text-slate-600'}`}>
              {tab === 'letter' ? 'Letter' : tab === 'citations' ? `Citations (${result.citations.length})` : 'Before You Submit'}
              {activeTab === tab && <div className="absolute bottom-0 left-3 right-3 h-0.5 bg-teal-500 rounded-full" />}
            </button>
          ))}
        </div>

        {activeTab === 'letter' && (
          <div className="p-5">
            {demoMode ? (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-2 mb-3">
                <p className="text-xs text-teal-700"><span className="font-bold">Demo Mode:</span> This is an illustrative sample letter. Sign up for a free trial to generate, edit, and download real AI-powered letters.</p>
              </div>
            ) : (<>
            <p className="text-xs text-slate-400 mb-1 font-medium">
              Edit directly below. Fill in the bracketed placeholders before submitting.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              <p className="text-xs text-amber-700"><span className="font-bold">&#9888; Do not enter PHI:</span> Do not include patient names, dates of birth, or medical record numbers here. Add patient-identifiable information only after downloading.</p>
            </div>
            </>)}
            <textarea
              className={`w-full h-[540px] font-serif text-[13px] leading-[1.8]
                border border-slate-200 rounded-xl p-5 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-300
                resize-none text-slate-700 bg-slate-50/50 transition-all dark:bg-slate-700 dark:border-slate-600 dark:text-white
                ${demoMode ? 'select-none cursor-default' : ''}`}
              value={editedLetter}
              readOnly={demoMode}
              onChange={e => setEditedLetter(e.target.value)}
            />
          </div>
        )}

        {activeTab === 'citations' && (
          <div className="p-5 space-y-3">
            {result.citations.length === 0
              ? <p className="text-sm text-slate-400">No citations retrieved for this combination.</p>
              : result.citations.map(c => (
                <div key={c.number} className="flex gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="w-7 h-7 rounded-lg bg-teal-500 text-white text-xs flex items-center justify-center flex-shrink-0 font-bold mt-0.5">
                    {c.number}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">{c.source}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{c.title}</p>
                    {c.url && (
                      <a href={c.url} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-teal-500 hover:text-teal-600 underline mt-1 inline-block font-medium">
                        View source
                      </a>
                    )}
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {activeTab === 'checklist' && (
          <div className="p-5 space-y-3">
            <p className="text-sm text-slate-500 mb-4">
              Complete these fields in the letter before submitting to the payer.
            </p>
            {result.provider_fill_ins.map((fi, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-amber-50/60 border border-amber-200/60 rounded-xl">
                <span className="w-4 h-4 rounded border-2 border-amber-300 flex-shrink-0 mt-0.5 inline-block" />
                <div>
                  <p className="text-sm font-mono font-bold text-amber-700">{fi.placeholder}</p>
                  <p className="text-xs text-amber-500 mt-0.5">{fi.description}</p>
                </div>
              </div>
            ))}
            <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700 font-medium">
              &#10003; Once all items above are complete, your LMN is ready to submit via fax, mail, or payer portal.
            </div>
          </div>
        )}
      </div>

      {/* What's Next? action panel */}
      <WhatsNext
        collapsed={whatsNextCollapsed}
        onToggle={() => setWhatsNextCollapsed(!whatsNextCollapsed)}
        onTrackAppeal={onOpenAppeals}
      />

      <p className="text-[10px] text-slate-400 leading-relaxed mt-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
        This letter is a template generated by RxCheckUp&#8482;. The prescribing physician must review all content for accuracy, add patient-specific information after downloading, and sign before submission. RxCheckUp does not provide medical advice.
      </p>
    </div>
  )
}
